import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Ticket, Plus, Trash2, Edit, AlertCircle, Eye, EyeOff, Crown } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const EventTiersPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: 0,
    quantity: 100,
    description: '',
    is_active: true,
    free_service_for_subscribers: false
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, tiersRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getTicketTiers(eventId),
      ]);
      setEvent(eventRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar tandas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingTier(null);
    setFormData({
      name: '', price: 0, quantity: 100, description: '',
      is_active: true, free_service_for_subscribers: false,
      promo_pay_x: 0, promo_get_y: 0, promo_valid_until: '', promo_max_redemptions: 0,
    });
    setShowModal(true);
  };

  const handleOpenEdit = (tier) => {
    setEditingTier(tier);
    setFormData({
      name: tier.name,
      price: tier.price,
      quantity: tier.quantity,
      description: tier.description || '',
      is_active: tier.is_active,
      free_service_for_subscribers: tier.free_service_for_subscribers || false,
      promo_pay_x: tier.promo_pay_x || 0,
      promo_get_y: tier.promo_get_y || 0,
      promo_valid_until: tier.promo_valid_until ? String(tier.promo_valid_until).slice(0, 16) : '',
      promo_max_redemptions: tier.promo_max_redemptions || 0,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Ingresa un nombre para la tanda');
      return;
    }
    // Validate promo config: get_y must be > pay_x when either is set
    const px = Number(formData.promo_pay_x || 0);
    const gy = Number(formData.promo_get_y || 0);
    if ((px > 0 || gy > 0) && gy <= px) {
      toast.error('Promo inválida: "Llevás" debe ser mayor que "Pagás"');
      return;
    }
    const payload = {
      ...formData,
      promo_pay_x: px,
      promo_get_y: gy,
      promo_valid_until: formData.promo_valid_until ? new Date(formData.promo_valid_until).toISOString() : null,
      promo_max_redemptions: Number(formData.promo_max_redemptions || 0),
      event_id: eventId,
    };
    try {
      if (editingTier) {
        await eventsApi.updateTicketTier(eventId, editingTier.id, payload);
        toast.success('Tanda actualizada');
      } else {
        await eventsApi.createTicketTier(eventId, payload);
        toast.success('Tanda creada');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tanda');
    }
  };

  const handleDelete = async (tierId) => {
    if (!confirm('¿Eliminar esta tanda de tickets?')) return;
    try {
      await eventsApi.deleteTicketTier(eventId, tierId);
      toast.success('Tanda eliminada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tanda');
    }
  };

  const handleToggleVisible = async (tier) => {
    const next = !(tier.is_visible === false ? false : true);
    setTiers(prev => prev.map(t => t.id === tier.id ? { ...t, is_visible: next } : t));
    try {
      await eventsApi.updateTicketTier(eventId, tier.id, {
        name: tier.name,
        price: tier.price,
        quantity: tier.quantity,
        description: tier.description || '',
        is_active: tier.is_active,
        is_visible: next,
        free_service_for_subscribers: tier.free_service_for_subscribers || false,
        event_id: eventId,
      });
      toast.success(next ? 'Tanda visible en la venta' : 'Tanda oculta de la venta');
      fetchData();
    } catch (error) {
      toast.error('No se pudo cambiar la visibilidad');
      fetchData();
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="grid grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-48 bg-[#1A1A1A] rounded" />)}
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const totalCapacity = tiers.reduce((sum, t) => sum + t.quantity, 0);
  const totalSold = tiers.reduce((sum, t) => sum + (t.sold || 0), 0);

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-tiers-page">
          <EventSubNav eventTitle={event?.title} />

          {/* Info Banner */}
          {tiers.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-500/10 border border-blue-500/30 p-4 mb-8 flex items-start gap-3"
            >
              <AlertCircle size={20} className="text-blue-500 mt-0.5" />
              <div>
                <p className="text-blue-500 font-medium">Sin tandas configuradas</p>
                <p className="text-blue-500/70 text-sm">
                  Este evento usa el precio base de ${event?.ticket_price?.toLocaleString()}. 
                  Crea tandas para ofrecer precios diferenciados (Early Bird, General, VIP, etc.)
                </p>
              </div>
            </motion.div>
          )}

          {/* Stats */}
          {tiers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-3 gap-6 mb-8"
            >
              <div className="card-dashboard">
                <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Total Tandas</p>
                <p className="text-3xl font-black">{tiers.length}</p>
              </div>
              <div className="card-dashboard">
                <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Capacidad Total</p>
                <p className="text-3xl font-black">{totalCapacity}</p>
              </div>
              <div className="card-dashboard">
                <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Vendidos</p>
                <p className="text-3xl font-black">{totalSold}</p>
              </div>
            </motion.div>
          )}

          {/* Header Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between mb-6"
          >
            <h2 className="text-xl font-bold uppercase tracking-tight">Tandas de Tickets</h2>
            <Button onClick={handleOpenCreate} className="btn-primary">
              <Plus size={16} className="mr-2" />
              Nueva Tanda
            </Button>
          </motion.div>

          {/* Tiers Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[...tiers].sort((a, b) => {
              const hiddenA = a.is_visible === false ? 1 : 0;
              const hiddenB = b.is_visible === false ? 1 : 0;
              if (hiddenA !== hiddenB) return hiddenA - hiddenB;
              const soldOutA = (a.is_sold_out || ((a.quantity || 0) > 0 && (a.sold || 0) >= (a.quantity || 0))) ? 1 : 0;
              const soldOutB = (b.is_sold_out || ((b.quantity || 0) > 0 && (b.sold || 0) >= (b.quantity || 0))) ? 1 : 0;
              if (soldOutA !== soldOutB) return soldOutA - soldOutB;
              return 0;
            }).map((tier) => {
              const isHidden = tier.is_visible === false;
              return (
              <div
                key={tier.id}
                className={`card-dashboard relative ${!tier.is_active && 'opacity-50'} ${isHidden ? 'opacity-50 border border-dashed border-[#27272A]' : ''}`}
                data-testid={`admin-tier-card-${tier.id}`}
              >
                {/* Status badges */}
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  {tier.promo_pay_x > 0 && tier.promo_get_y > tier.promo_pay_x && (
                    <div
                      className="px-2 py-1 text-xs font-black uppercase bg-[#D4AF37] text-black rounded"
                      title={`Promo ${tier.promo_get_y}x${tier.promo_pay_x}`}
                      data-testid={`admin-tier-promo-badge-${tier.id}`}
                    >
                      {tier.promo_get_y}x{tier.promo_pay_x}
                    </div>
                  )}
                  {tier.free_service_for_subscribers && (
                    <div className="px-2 py-1 text-xs font-bold uppercase bg-yellow-500/20 text-yellow-500 flex items-center gap-1">
                      <Crown size={12} />
                      BRAVX
                    </div>
                  )}
                  {isHidden && (
                    <div className="px-2 py-1 text-xs font-bold uppercase bg-zinc-700/50 text-zinc-200">
                      Oculta
                    </div>
                  )}
                  {((tier.is_sold_out || ((tier.quantity || 0) > 0 && (tier.sold || 0) >= (tier.quantity || 0))) && !isHidden) && (
                    <div className="px-2 py-1 text-xs font-bold uppercase bg-red-500/20 text-red-400">
                      Agotado
                    </div>
                  )}
                  <div className={`px-2 py-1 text-xs font-bold uppercase ${
                    tier.is_active ? 'bg-green-500/20 text-green-500' : 'bg-[#27272A] text-[#52525B]'
                  }`}>
                    {tier.is_active ? 'Activo' : 'Inactivo'}
                  </div>
                </div>

                {/* Tier icon */}
                <div className="w-12 h-12 bg-[#1A1A1A] flex items-center justify-center mb-4">
                  <Ticket size={24} className="text-white" />
                </div>

                {/* Tier info */}
                <h3 className="font-black text-xl uppercase tracking-tight mb-2">{tier.name}</h3>
                <p className="text-4xl font-black mb-1">${tier.price.toLocaleString()}</p>
                {event?.service_fee > 0 && (
                  <p className="text-[#A1A1AA] text-sm mb-2">
                    + {event.service_fee_type === 'percentage' 
                      ? `${event.service_fee}%` 
                      : `$${event.service_fee.toLocaleString()}`} servicio
                    {tier.free_service_for_subscribers && <span className="text-[#D4AF37] ml-1">(BRAVX gratis)</span>}
                  </p>
                )}
                
                {tier.description && (
                  <p className="text-[#A1A1AA] text-sm mb-4">{tier.description}</p>
                )}

                {/* Progress bar */}
                {(() => {
                  const pct = (tier.quantity || 0) > 0
                    ? Math.min(100, Math.round(((tier.sold || 0) / tier.quantity) * 100))
                    : 0;
                  const lastSold = tier.last_sold_at ? new Date(tier.last_sold_at) : null;
                  const isHot = lastSold && (Date.now() - lastSold.getTime()) < 10 * 60 * 1000;
                  return (
                    <div className="mb-4">
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="text-[#A1A1AA] flex items-center gap-2">
                          Vendidos
                          {isHot && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-wider rounded-full border border-emerald-500/30"
                              title={`Última venta: ${lastSold.toLocaleString('es-AR')}`}
                              data-testid={`admin-tier-hot-${tier.id}`}
                            >
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                              </span>
                              En vivo
                            </span>
                          )}
                        </span>
                        <span className="font-mono">{tier.sold || 0} / {tier.quantity} <span className="text-[#52525B]">({pct}%)</span></span>
                      </div>
                      <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden" data-testid={`admin-tier-progress-${tier.id}`}>
                        <div
                          className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] transition-[width] duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-4 border-t border-[#27272A]">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleVisible(tier)}
                    className={isHidden ? 'text-zinc-500 hover:text-white' : 'text-[#D4AF37] hover:text-[#F5D25A]'}
                    title={isHidden ? 'Mostrar en la venta' : 'Ocultar de la venta'}
                    data-testid={`admin-tier-toggle-visible-${tier.id}`}
                  >
                    {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenEdit(tier)}
                    className="flex-1 border-[#27272A]"
                  >
                    <Edit size={14} className="mr-2" />
                    Editar
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDelete(tier.id)}
                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    disabled={(tier.sold || 0) > 0}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              );
            })}

            {/* Empty state card */}
            {tiers.length === 0 && (
              <div className="card-dashboard text-center py-12 col-span-full">
                <Ticket size={48} className="mx-auto mb-4 text-[#27272A]" />
                <p className="text-[#52525B] text-lg">Crea tu primera tanda</p>
                <p className="text-[#52525B] text-sm mt-2">Early Bird, General, VIP...</p>
                <Button onClick={handleOpenCreate} className="btn-secondary mt-6">
                  <Plus size={16} className="mr-2" />
                  Crear Tanda
                </Button>
              </div>
            )}
          </motion.div>
        </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold uppercase mb-6">
              {editingTier ? 'Editar Tanda' : 'Nueva Tanda de Tickets'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Nombre</Label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Early Bird"
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none text-lg"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Precio</Label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Cantidad</Label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Descripción (opcional)</Label>
                <input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Primeras 100 entradas..."
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <Label className="text-sm text-[#A1A1AA]">Tanda activa</Label>
                <Switch 
                  checked={formData.is_active} 
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#27272A]">
                <div className="flex items-center gap-2">
                  <Crown size={16} className="text-yellow-500" />
                  <div>
                    <Label className="text-sm text-white">Servicio gratis para BRAVX</Label>
                    <p className="text-xs text-[#52525B]">Sin cargo de servicio para suscriptores</p>
                  </div>
                </div>
                <Switch 
                  checked={formData.free_service_for_subscribers} 
                  onCheckedChange={(checked) => setFormData({...formData, free_service_for_subscribers: checked})}
                />
              </div>

              {/* Promo "Pay X, Get Y" */}
              <div className="pt-4 border-t border-[#27272A]" data-testid="promo-config-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Promoción</span>
                  <span className="text-[10px] text-[#52525B]">Pagá X, llevás Y</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Pagás</Label>
                    <input
                      type="number"
                      min={0}
                      value={formData.promo_pay_x ?? 0}
                      onChange={(e) => setFormData({...formData, promo_pay_x: parseInt(e.target.value) || 0})}
                      placeholder="Ej: 1"
                      className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] px-0 py-3 text-white outline-none"
                      data-testid="promo-pay-x"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Llevás</Label>
                    <input
                      type="number"
                      min={0}
                      value={formData.promo_get_y ?? 0}
                      onChange={(e) => setFormData({...formData, promo_get_y: parseInt(e.target.value) || 0})}
                      placeholder="Ej: 2"
                      className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] px-0 py-3 text-white outline-none"
                      data-testid="promo-get-y"
                    />
                  </div>
                </div>
                {Number(formData.promo_pay_x) > 0 && Number(formData.promo_get_y) > Number(formData.promo_pay_x) && (
                  <p className="text-[11px] text-[#D4AF37] mt-2">
                    Preview: el comprador paga {formData.promo_pay_x} y recibe {formData.promo_get_y} ({Number(formData.promo_get_y) - Number(formData.promo_pay_x)} de regalo, ingresa un DNI por cada entrada).
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Vigente hasta (opcional)</Label>
                    <input
                      type="datetime-local"
                      value={formData.promo_valid_until || ''}
                      onChange={(e) => setFormData({...formData, promo_valid_until: e.target.value})}
                      className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] px-0 py-3 text-white outline-none text-sm"
                      data-testid="promo-valid-until"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Máx. canjes (0 = ∞)</Label>
                    <input
                      type="number"
                      min={0}
                      value={formData.promo_max_redemptions ?? 0}
                      onChange={(e) => setFormData({...formData, promo_max_redemptions: parseInt(e.target.value) || 0})}
                      className="w-full bg-transparent border-b border-[#27272A] focus:border-[#D4AF37] px-0 py-3 text-white outline-none"
                      data-testid="promo-max-redemptions"
                    />
                  </div>
                </div>
                {editingTier?.promo_redemptions_count > 0 && (
                  <p className="text-[10px] text-[#52525B] mt-2">
                    Canjeada {editingTier.promo_redemptions_count} vez/veces hasta ahora.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-[#27272A]">
                Cancelar
              </Button>
              <Button onClick={handleSave} className="flex-1 btn-primary">
                {editingTier ? 'Guardar Cambios' : 'Crear Tanda'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
        </AdminLayout>
      </SidebarProvider>
  );
};

export default EventTiersPage;
